// this is an inlined version of vue2-scrollspy which has been updated for Vue 3
// see https://github.com/ibufu/vue2-scrollspy/blob/master/src/index.js

import { scrollWithAnimation, Easing } from './animate';

const install = (Vue, options) => {
  if (install.installed) return;

  const bodyScrollEl = {};

  // For ff, ie
  Object.defineProperty(bodyScrollEl, 'scrollTop', {
    get() {
      return document.body.scrollTop || document.documentElement.scrollTop;
    },
    set(val) {
      document.body.scrollTop = val;
      document.documentElement.scrollTop = val;
    }
  });

  Object.defineProperty(bodyScrollEl, 'scrollHeight', {
    get() {
      return document.body.scrollHeight || document.documentElement.scrollHeight;
    }
  });

  Object.defineProperty(bodyScrollEl, 'offsetHeight', {
    get() {
      return window.innerHeight;
    }
  });

  const scrollSpyContext = '@@scrollSpyContext';
  const scrollSpyElements = {};
  const scrollSpySections = {};
  const activeElement = {};
  const activableElements = {};
  const currentIndex = {};

  // eslint-disable-next-line no-param-reassign
  options = {
    allowNoActive: false,
    sectionSelector: null,
    data: null,
    offset: 0,
    time: 500,
    steps: 30,
    easing: null,
    active: {
      selector: null,
      class: 'active'
    },
    link: {
      selector: 'a'
    },
    ...(options || {})
  };

  function findElements(container, selector) {
    if (!selector) {
      return container.children;
    }

    // eslint-disable-next-line no-use-before-define
    const id = scrollSpyId(container);

    const elements = [];

    // eslint-disable-next-line no-restricted-syntax
    for (const el of container.querySelectorAll(selector)) {
      // Filter out elements that are owned by another directive
      // eslint-disable-next-line no-use-before-define
      if (scrollSpyIdFromAncestors(el) === id) {
        elements.push(el);
      }
    }

    return elements;
  }

  function scrollSpyId(el) {
    return el.getAttribute('data-scroll-spy-id') || el.getAttribute('scroll-spy-id') || 'default';
  }

  function scrollSpyIdDefined(el) {
    return !!el.getAttribute('data-scroll-spy-id') || !!el.getAttribute('scroll-spy-id');
  }

  function scrollSpyIdFromAncestors(el) {
    do {
      if (scrollSpyIdDefined(el)) {
        return scrollSpyId(el);
      }
      // eslint-disable-next-line no-param-reassign
      el = el.parentElement;
    } while (el);
    return 'default';
  }

  function initScrollSections(el, sectionSelector) {
    const id = scrollSpyId(el);
    const scrollSpyContextEl = el[scrollSpyContext];
    const idScrollSections = findElements(el, sectionSelector);
    scrollSpySections[id] = idScrollSections;

    if (idScrollSections[0] && idScrollSections[0].offsetParent !== el) {
      scrollSpyContextEl.eventEl = window;
      scrollSpyContextEl.scrollEl = bodyScrollEl;
    }
  }

  function getOffsetTop(elem, untilParent) {
    let offsetTop = 0;
    do {
      // eslint-disable-next-line no-restricted-globals
      if (!isNaN(elem.offsetTop)) {
        offsetTop += elem.offsetTop;
      }
      // eslint-disable-next-line no-param-reassign
      elem = elem.offsetParent;
    } while (elem && elem !== untilParent);
    return offsetTop;
  }

  function scrollTo(el, index) {
    const id = scrollSpyId(el);
    const idScrollSections = scrollSpySections[id];

    // eslint-disable-next-line no-shadow
    const { scrollEl, options } = el[scrollSpyContext];
    const current = scrollEl.scrollTop;

    if (idScrollSections[index]) {
      const target = getOffsetTop(idScrollSections[index]) - options.offset;
      if (options.easing) {
        scrollWithAnimation(scrollEl, current, target, options.time, options.easing);
        return;
      }

      const { time } = options;
      const { steps } = options;
      const timems = parseInt(time / steps, 10);
      const gap = target - current;
      for (let i = 0; i <= steps; i += 1) {
        const pos = current + (gap / steps) * i;
        setTimeout(() => {
          scrollEl.scrollTop = pos;
        }, timems * i);
      }
    }
  }

  Vue.directive('scroll-spy', {
    beforeMount(el, binding) {
      function onScroll() {
        const id = scrollSpyId(el);
        const idScrollSections = scrollSpySections[id];

        // eslint-disable-next-line no-shadow
        const { scrollEl, options } = el[scrollSpyContext];

        let index;

        if (scrollEl.offsetHeight + scrollEl.scrollTop >= scrollEl.scrollHeight - 10) {
          index = idScrollSections.length;
        } else {
          for (index = 0; index < idScrollSections.length; index += 1) {
            if (
              getOffsetTop(idScrollSections[index], scrollEl) - options.offset >
              scrollEl.scrollTop
            ) {
              break;
            }
          }
        }

        index -= 1;

        if (index < 0) {
          index = options.allowNoActive ? null : 0;
        } else if (
          options.allowNoActive &&
          index >= idScrollSections.length - 1 &&
          getOffsetTop(idScrollSections[index]) + idScrollSections[index].offsetHeight <
            scrollEl.scrollTop
        ) {
          index = null;
        }

        if (index !== currentIndex[id]) {
          let idActiveElement = activeElement[id];
          if (idActiveElement) {
            idActiveElement.classList.remove(idActiveElement[scrollSpyContext].options.class);
            activeElement[id] = null;
          }

          currentIndex[id] = index;
          if (typeof currentIndex !== 'undefined' && Object.keys(activableElements).length > 0) {
            idActiveElement = activableElements[id][currentIndex[id]];
            activeElement[id] = idActiveElement;

            if (idActiveElement) {
              idActiveElement.classList.add(idActiveElement[scrollSpyContext].options.class);
            }
          }

          if (options.data) {
            // eslint-disable-next-line no-param-reassign
            binding.instance[options.data] = index;
          }
        }
      }

      // eslint-disable-next-line no-param-reassign
      binding.instance.$scrollTo = scrollTo.bind(null, el);

      const id = scrollSpyId(el);

      // eslint-disable-next-line no-param-reassign
      el[scrollSpyContext] = {
        onScroll,
        options: { ...options, ...binding.value },
        id: scrollSpyId(el),
        eventEl: el,
        scrollEl: el
      };

      scrollSpyElements[id] = el;
      delete currentIndex[id];
    },
    mounted(el) {
      const {
        options: { sectionSelector }
      } = el[scrollSpyContext];
      initScrollSections(el, sectionSelector);
      const { eventEl, onScroll } = el[scrollSpyContext];
      eventEl.addEventListener('scroll', onScroll);

      onScroll();
    },
    updated(el, binding) {
      // eslint-disable-next-line no-param-reassign
      el[scrollSpyContext].options = { ...options, ...binding.value };
      const {
        onScroll,
        options: { sectionSelector }
      } = el[scrollSpyContext];

      initScrollSections(el, sectionSelector);
      onScroll();
    },
    unmounted(el) {
      const { eventEl, onScroll } = el[scrollSpyContext];
      eventEl.removeEventListener('scroll', onScroll);
    }
  });

  function scrollSpyActive(el, binding) {
    const activeOptions = { ...options.active, ...binding.value };
    // eslint-disable-next-line no-use-before-define
    initScrollActiveElement(el, activeOptions);
  }

  function initScrollActiveElement(el, activeOptions) {
    const id = scrollSpyId(el);
    activableElements[id] = findElements(el, activeOptions.selector);
    const arr = [...activableElements[id]];
    // eslint-disable-next-line no-shadow
    arr.forEach(el => {
      // eslint-disable-next-line no-param-reassign
      el[scrollSpyContext] = {
        options: activeOptions
      };
    });
  }

  Vue.directive('scroll-spy-active', {
    mounted: scrollSpyActive,
    updated: scrollSpyActive
  });

  // eslint-disable-next-line no-shadow
  function scrollLinkClickHandler(index, scrollSpyId) {
    scrollTo(scrollSpyElements[scrollSpyId], index);
  }

  function initScrollLink(el, selector) {
    const id = scrollSpyId(el);

    const linkElements = findElements(el, selector);

    for (let i = 0; i < linkElements.length; i += 1) {
      const linkElement = linkElements[i];

      const listener = scrollLinkClickHandler.bind(null, i, id);
      if (!linkElement[scrollSpyContext]) {
        linkElement[scrollSpyContext] = {};
      }

      if (!linkElement[scrollSpyContext].click) {
        linkElement.addEventListener('click', listener);
        linkElement[scrollSpyContext].click = listener;
      }
    }
  }

  Vue.directive('scroll-spy-link', {
    mounted(el, binding) {
      const linkOptions = { ...options.link, ...binding.value };
      initScrollLink(el, linkOptions.selector);
    },
    updated(el, binding) {
      const linkOptions = { ...options.link, ...binding.value };
      initScrollLink(el, linkOptions.selector);
    },
    unmounted(el) {
      const linkElements = findElements(el);

      for (let i = 0; i < linkElements.length; i += 1) {
        const linkElement = linkElements[i];
        const id = scrollSpyId(el);
        const listener = scrollLinkClickHandler.bind(null, i, id);
        if (!linkElement[scrollSpyContext]) {
          linkElement[scrollSpyContext] = {};
        }

        if (linkElement[scrollSpyContext].click) {
          linkElement.removeEventListener('click', listener);
          delete linkElement[scrollSpyContext].click;
        }
      }
    }
  });
};

if (typeof window !== 'undefined' && window.Vue) {
  install(window.Vue);
}

export default install;

export { Easing, install };
